/**
 * Module-level in-memory cache.
 * Lives for the entire browser session — survives component unmounts/remounts.
 * Keyed by string, with per-entry timestamps for TTL checks.
 */

interface Entry<T> {
  data: T;
  at: number; // Date.now() when written
}

const store = new Map<string, Entry<unknown>>();

/** Read a cache entry. Returns `undefined` if missing or older than `maxAgeMs`. */
export function cacheRead<T>(key: string, maxAgeMs: number): T | undefined {
  const hit = store.get(key);
  if (!hit) return undefined;
  if (Date.now() - hit.at > maxAgeMs) return undefined;
  return hit.data as T;
}

/** Write a value into the cache. */
export function cacheWrite<T>(key: string, data: T): void {
  store.set(key, { data, at: Date.now() });
}

/**
 * Delete all entries whose key starts with `prefix`.
 * Use after mutations to invalidate a whole "namespace".
 * e.g. cacheInvalidate('users:') clears all user list caches.
 */
export function cacheInvalidate(prefix: string): void {
  Array.from(store.keys()).forEach((k) => {
    if (k.startsWith(prefix)) store.delete(k);
  });
}

/** How many milliseconds ago this key was written (null if not in cache). */
export function cacheAge(key: string): number | null {
  const hit = store.get(key);
  return hit ? Date.now() - hit.at : null;
}

export function cacheClear(): void {
  store.clear();
}
